import { Button } from "@/components/ui/button";
import { MultiSelect, type Option } from "@/components/ui/multi-select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Filter } from "lucide-react";
import { useEffect, useState } from "react";
import { CodingDashboardDatePickers } from "./CodingDashboardDatePickers";

export interface DashboardFilterValues {
  selectedCustomers: Option[];
  fromDate: Date | null;
  toDate: Date | null;
}

interface CodingDashboardFilterPopoverProps {
  customerOptions: Option[];
  selectedCustomers: Option[];
  fromDate: Date | null;
  toDate: Date | null;
  onApply: (filters: DashboardFilterValues) => void;
  onClear: () => void;
  isCustomised: boolean;
  isLoadingCustomers: boolean;
}

export function CodingDashboardFilterPopover({
  customerOptions,
  selectedCustomers,
  fromDate,
  toDate,
  onApply,
  onClear,
  isCustomised,
  isLoadingCustomers,
}: CodingDashboardFilterPopoverProps) {
  const [open, setOpen] = useState(false);

  // Draft state — initialised from props when popover opens
  const [draftCustomers, setDraftCustomers] = useState<Option[]>([]);
  const [draftFromDate, setDraftFromDate] = useState<Date | null>(null);
  const [draftToDate, setDraftToDate] = useState<Date | null>(null);

  // Sync draft state from props only when popover opens (not when props change while open)
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-seed the draft only on open; tracking the live props would overwrite an in-progress edit
  useEffect(() => {
    if (open) {
      setDraftCustomers([...selectedCustomers]);
      setDraftFromDate(fromDate);
      setDraftToDate(toDate);
    }
  }, [open]);

  const handleApply = () => {
    onApply({ selectedCustomers: draftCustomers, fromDate: draftFromDate, toDate: draftToDate });
    setOpen(false);
  };

  const handleClear = () => {
    onClear();
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" aria-label="Dashboard filters" className="relative">
          <Filter className="mr-2 h-4 w-4" />
          Filters
          {isCustomised && (
            <span
              data-testid="filter-customised-dot"
              className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-primary"
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-4" align="end">
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Date range</label>
            <CodingDashboardDatePickers
              fromDate={draftFromDate}
              toDate={draftToDate}
              onFromDateChange={setDraftFromDate}
              onToDateChange={setDraftToDate}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Customers</label>
            <MultiSelect
              options={customerOptions}
              value={draftCustomers}
              onChange={setDraftCustomers}
              placeholder="Select customers..."
              isLoading={isLoadingCustomers}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="ghost" size="sm" onClick={handleClear}>
              Clear all
            </Button>
            <Button size="sm" onClick={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
